/**
 * When the microphone is not available.
 *
 * ## Why the Safari instructions are spelled out
 *
 * Denying a permission prompt on iPadOS is a one-tap mistake and an eight-tap recovery,
 * and the recovery is hidden behind the "aA" menu in the address bar — which is not a
 * place anybody looks for microphone settings. Telling someone to "enable it in your
 * browser settings" is, on this specific device, functionally the same as telling them
 * nothing. So the steps are here, in order, naming the actual menu.
 *
 * ## Why this screen is not a wall
 *
 * It leads with On the Go and ends with manual entry. A denied permission should cost you
 * the *listening*, not the app — and now that there is a whole mode built for having no
 * instrument, the fix-your-settings instructions are no longer the fastest way forward.
 * They stay, below, for the person who came here to fix it.
 */

import type { SessionView } from './sessionView.js';
import { ContinueOnTheGo } from './ModeToggle.js';
import { ManualEntryPanel } from './ManualEntry.js';

export interface PermissionScreenProps {
  view: SessionView;
  /** Ask again. The prompt only reappears if the browser has not hard-denied. */
  onRetry?: () => void;
}

const SAFARI_STEPS: readonly string[] = Object.freeze([
  'Tap the aA button at the left of the address bar.',
  'Choose Website Settings.',
  'Set Microphone to Allow.',
  'Reload this page.',
]);

export function PermissionDeniedScreen({
  view,
  onRetry,
}: PermissionScreenProps): React.JSX.Element {
  return (
    <section className="hc-screen" data-testid="hc-permission-denied">
      <h1 className="hc-screen__title">The microphone is switched off</h1>
      <p className="hc-screen__lead">
        Safari is not letting this page hear the piano. It takes about ten seconds to turn back on —
        or carry on without it, which loses nothing but the listening.
      </p>
      <ContinueOnTheGo view={view} />
      <ol className="hc-steps" data-testid="hc-safari-steps">
        {SAFARI_STEPS.map((step) => (
          <li key={step}>{step}</li>
        ))}
      </ol>
      {onRetry ? (
        <button
          type="button"
          // Demoted only when there is something better to be primary. With no On the Go
          // wired this is still the way forward, and a screen whose only action is quiet
          // reads as a screen with no action.
          className={`hc-button ${view.startOnTheGo ? 'hc-button--quiet' : 'hc-button--primary'}`}
          onClick={onRetry}
        >
          Try again
        </button>
      ) : null}
      <ManualEntryPanel view={view} />
    </section>
  );
}

export function MicUnavailableScreen({ view }: PermissionScreenProps): React.JSX.Element {
  return (
    <section className="hc-screen" data-testid="hc-mic-unavailable">
      <h1 className="hc-screen__title">No microphone found</h1>
      <p className="hc-screen__lead">
        Nothing on this device is offering audio input — another app may be holding it.
      </p>
      <ContinueOnTheGo view={view} />
      <ManualEntryPanel view={view} />
    </section>
  );
}

/** Picks the right screen for the current `micStatus`. */
export function PermissionScreen({ view, onRetry }: PermissionScreenProps): React.JSX.Element {
  return view.state.micStatus === 'unavailable' ? (
    <MicUnavailableScreen view={view} />
  ) : (
    <PermissionDeniedScreen view={view} onRetry={onRetry} />
  );
}
